/**
 * 구조화된 로그 시스템
 * 개발 환경에서만 콘솔에 출력되며, 카테고리별 필터링 지원
 */

export type LogLevel = 'info' | 'warn' | 'error' | 'debug'
export type LogCategory = 'message' | 'order' | 'api' | 'ui' | 'auth' | 'general'

// 로그 데이터 타입 - 다양한 데이터 타입을 허용하는 유니온 타입
export type LogData =
  | string
  | number
  | boolean
  | null
  | undefined
  | Record<string, unknown>
  | unknown[]
  | Error

interface LogEntry {
  level: LogLevel
  category: LogCategory
  message: string
  data?: LogData
  timestamp: Date
}

class Logger {
  private isDev = process.env.NODE_ENV === 'development'
  private enabledCategories: Set<LogCategory> = new Set([
    'message',
    'order',
    'api',
    'ui',
    'auth',
    'general',
  ])

  /**
   * 카테고리 활성화/비활성화
   */
  setCategory(category: LogCategory, enabled: boolean) {
    if (enabled) {
      this.enabledCategories.add(category)
    } else {
      this.enabledCategories.delete(category)
    }
  }

  /**
   * 로그 출력
   */
  private log(entry: LogEntry) {
    if (!this.isDev || !this.enabledCategories.has(entry.category)) {
      return
    }

    const prefix = `[${entry.category.toUpperCase()}]`
    const timestamp = entry.timestamp.toISOString().split('T')[1].split('.')[0]

    switch (entry.level) {
      case 'error':
        console.error(`🔴 ${prefix} [${timestamp}]`, entry.message, entry.data || '')
        break
      case 'warn':
        console.warn(`🟡 ${prefix} [${timestamp}]`, entry.message, entry.data || '')
        break
      case 'info':
        console.info(`🔵 ${prefix} [${timestamp}]`, entry.message, entry.data || '')
        break
      case 'debug':
        console.debug(`⚪ ${prefix} [${timestamp}]`, entry.message, entry.data || '')
        break
    }
  }

  info(category: LogCategory, message: string, data?: LogData) {
    this.log({ level: 'info', category, message, data, timestamp: new Date() })
  }

  warn(category: LogCategory, message: string, data?: LogData) {
    this.log({ level: 'warn', category, message, data, timestamp: new Date() })
  }

  error(category: LogCategory, message: string, data?: LogData) {
    this.log({ level: 'error', category, message, data, timestamp: new Date() })
  }

  debug(category: LogCategory, message: string, data?: LogData) {
    this.log({ level: 'debug', category, message, data, timestamp: new Date() })
  }

  /**
   * 메시지 관련 로그 헬퍼
   */
  message = {
    info: (message: string, data?: LogData) => this.info('message', message, data),
    warn: (message: string, data?: LogData) => this.warn('message', message, data),
    error: (message: string, data?: LogData) => this.error('message', message, data),
    debug: (message: string, data?: LogData) => this.debug('message', message, data),
  }

  /**
   * 주문 관련 로그 헬퍼
   */
  order = {
    info: (message: string, data?: LogData) => this.info('order', message, data),
    warn: (message: string, data?: LogData) => this.warn('order', message, data),
    error: (message: string, data?: LogData) => this.error('order', message, data),
    debug: (message: string, data?: LogData) => this.debug('order', message, data),
  }

  /**
   * UI 관련 로그 헬퍼
   */
  ui = {
    info: (message: string, data?: LogData) => this.info('ui', message, data),
    warn: (message: string, data?: LogData) => this.warn('ui', message, data),
    error: (message: string, data?: LogData) => this.error('ui', message, data),
    debug: (message: string, data?: LogData) => this.debug('ui', message, data),
  }

  /**
   * API 관련 로그 헬퍼
   */
  api = {
    info: (message: string, data?: LogData) => this.info('api', message, data),
    warn: (message: string, data?: LogData) => this.warn('api', message, data),
    error: (message: string, data?: LogData) => this.error('api', message, data),
    debug: (message: string, data?: LogData) => this.debug('api', message, data),
  }
}

// 싱글톤 인스턴스
export const logger = new Logger()
